import { effect, Signal, useSignalEffect } from '@preact/signals-react';
import { useContext, useEffect, useRef } from 'react';
import { useSignals } from '@preact/signals-react/runtime';
import { EditorDeclarativeBlock } from '@/webapi/use-experience-api';
import { EditorContext } from '@/features/editor/context/editor-context';
import {
  AiVisualEditMessage,
  ElementMessage,
  NewElementMessage,
} from '@/features/editor/context/use-device-preview';
import {
  AiComponentTab,
  VisualGroupInfo,
} from '@/features/editor/widgets/shared/ai-editing-layout/types';
import { ContextualAttachment } from '@/features/ai-agents/shared/models';
import { CompoundChange } from '@/pkg/sdk';
import {
  extractCSSAttachment,
  extractHTMLAttachment,
} from '@/features/editor/widgets/ai-section-editor/extraction';

export interface LifecycleCallbacks {
  removeByGroupName: (group: string, selector: string) => void;
  removeAllByGroupName: (group: string, selector: string) => void;
  selectTab: (tab: AiComponentTab) => void;
}

export function useComponentLifecycle(
  newChange: Signal<EditorDeclarativeBlock>,
  selectedTab: Signal<AiComponentTab>,
  isFullScreen: Signal<boolean>,
  highlightedVisualGroup: Signal<VisualGroupInfo | undefined>,
  contextualAttachments: Signal<ContextualAttachment[]>,
  sessionData: Signal<Record<string, any>>,
  callbacks: LifecycleCallbacks,
) {
  useSignals();
  const {
    inspectorNav: { gotoAppCatalog },
    experienceState: {
      upsertEditorChange,
      currentExperience,
      currentVariantIdx,
    },
    devicePreview: {
      editComponent,
      aiEditWaiting,
      syncSectionModifiers,
      runInIframe,
      editorState: { device, installedFonts, mostPopularFont },
    },
    previewLoading,
    deviceNavigation: { previewUrl, urlToStorePage },
  } = useContext(EditorContext);

  const mountsCount = useRef(0);

  // Update session data when context changes
  effect(() => {
    const pageType = urlToStorePage(previewUrl);

    const htmlSelectors = Array.from(
      new Set(
        contextualAttachments.value
          .filter((att) => att.type.includes(`html`))
          .map((att) => att.id),
      ),
    );

    sessionData.value = {
      device,
      selector: htmlSelectors?.[0],
      most_popular_font: mostPopularFont,
      installed_fonts: installedFonts,
      page_url: previewUrl,
      page_type: pageType.kind,
      page_title: pageType.caption,
      section_id: newChange.value.editorId,
      experience_id: currentExperience?.id,
      experience_variant_id:
        currentExperience?.variants?.[currentVariantIdx]?.id,
    };

    syncSectionModifiers(htmlSelectors);
  });

  effect(() => {
    if (isFullScreen.value && selectedTab.value !== AiComponentTab.CODE) {
      isFullScreen.value = false;
    }
  });

  const getSelectors = () =>
    contextualAttachments?.value
      ?.map((a) => a.id)
      .filter((a) => !a.endsWith(`-css`));

  effect(() => {
    if (mountsCount.current === 0) {
      const agentId = (newChange.value.block.value as CompoundChange)
        ?.selectedAgent?.id;
      if (agentId === `section-modifier`) {
        const selectors = getSelectors();
        aiEditWaiting(selectors);
      } else {
        editComponent(newChange.value.editorSelector);
      }
      mountsCount.current += 1;
    }
  });

  useEffect(() => {
    if (!previewLoading) {
      const selectors = getSelectors();
      aiEditWaiting(selectors);
    }
  }, [previewLoading]);

  const visualEditHandler = (ev: any) => {
    const detail = ev?.detail as AiVisualEditMessage;
    if (detail.subAction === `scroll`) {
      highlightedVisualGroup.value = {
        group: detail.group,
        selector: detail.selector,
      };
      if (selectedTab.value !== AiComponentTab.EDIT) {
        callbacks.selectTab(AiComponentTab.EDIT);
      }
      return;
    }
    if (detail.subAction === `remove`) {
      callbacks.removeByGroupName(detail.group, detail.selector);
    }
    if (detail.subAction === `removeAll`) {
      callbacks.removeAllByGroupName(detail.group, detail.selector);
    }
    if ([`addAfter`, `addBefore`].includes(detail.subAction)) {
      upsertEditorChange(newChange.value);
      gotoAppCatalog({
        action: `create`,
        kind: detail.subAction.replace(`add`, `append`),
        selector: detail.selector,
      } as NewElementMessage);
    }
  };

  const sectionModifierAddHandler = (ev: any) => {
    const detail = ev?.detail as ElementMessage;
    if (!detail?.selector) return;

    const htmlAttachment = extractHTMLAttachment(detail.selector, runInIframe);
    const cssAttachment = extractCSSAttachment(detail.selector, runInIframe);

    const currentAttachments = contextualAttachments.value;
    const existingIds = new Set(currentAttachments.map((att) => att.id));
    const newAttachments: ContextualAttachment[] = [];
    if (!existingIds.has(htmlAttachment.id)) {
      newAttachments.push(htmlAttachment);
    }
    if (!existingIds.has(cssAttachment.id)) {
      newAttachments.push(cssAttachment);
    }
    if (newAttachments.length > 0) {
      contextualAttachments.value = [...currentAttachments, ...newAttachments];
    }
  };

  const sectionModifierAddWithSelectorHandler = (ev: any) => {
    const detail = ev?.detail as { selector: string };
    if (!detail?.selector) return;

    try {
      const htmlAttachment = extractHTMLAttachment(
        detail.selector,
        runInIframe,
      );
      const cssAttachment = extractCSSAttachment(detail.selector, runInIframe);

      const currentAttachments = contextualAttachments.value;
      const existingIds = new Set(currentAttachments.map((att) => att.id));
      const newAttachments: ContextualAttachment[] = [];
      if (!existingIds.has(htmlAttachment.id)) {
        newAttachments.push(htmlAttachment);
      }
      if (!existingIds.has(cssAttachment.id)) {
        newAttachments.push(cssAttachment);
      }
      if (newAttachments.length > 0) {
        contextualAttachments.value = [
          ...currentAttachments,
          ...newAttachments,
        ];
      }
    } catch (error) {
      console.error(`Failed to add section with selector:`, error);
    }
  };

  useSignalEffect(() => {
    window[`vslyAiEditorSelectedTab`] = selectedTab.value.toLowerCase();
  });

  useSignalEffect(() => {
    window.addEventListener(`ai-visual-edit`, visualEditHandler);
    window.addEventListener(`aiSectionAdd`, sectionModifierAddHandler);
    window.addEventListener(
      `aiSectionAddWithSelector`,
      sectionModifierAddWithSelectorHandler,
    );
    return () => {
      window.removeEventListener(`ai-visual-edit`, visualEditHandler);
      window.removeEventListener(`aiSectionAdd`, sectionModifierAddHandler);
      window.removeEventListener(
        `aiSectionAddWithSelector`,
        sectionModifierAddWithSelectorHandler,
      );
      window[`vslyAiEditorSelectedTab`] = undefined;
    };
  });
}
